/**
 * A card's own shapes for the zones that stand free of the folds (asked for
 * 2026-10-11): the big circle as a heart or a square, the message band as a
 * diamond or a circle, the two half circles of Opening 1 as half hearts,
 * normal or bigger - and each of those two halves slid up or down along its
 * own crease, by the person, or lined up with the other.
 *
 * The options come with the template (templates/<id>/<paper>/<hash>.shapes.json,
 * made by scripts/build-shapes.mjs from the same zone model): each one's
 * outline on the printed page, the piece every opening shows, exactly as the
 * model folds them - and for a half, how far it may slide and which way that
 * is on the page. A slide is a translation, on the page and in every opening
 * (the half stays on its own flap), so it is done here; a card is never drawn
 * slid beyond the range the model allows. The template, its hash, its zone
 * numbers and every other zone stay as they are.
 */
import type { Pt, TemplateDocument, TemplateViewPiece } from './types.ts';

export interface ZoneShapeOption {
  key: string;
  kind: 'circle' | 'heart' | 'square' | 'diamond' | 'band';
  /** Half-width, mm. */
  size: number;
  /** Zones 10 and 11: 'normal' or 'big'. */
  sizeName?: 'normal' | 'big';
  outline: Pt[];
  extraOutlines: Pt[][];
  areaMm2: number;
  up: Pt;
  visibleMm2: Record<string, number>;
  /** By view id: the piece of this shape that view shows. */
  pieces: Record<string, TemplateViewPiece>;
  /**
   * How far it may slide along its crease (mm, + = down in the meeting view),
   * the page direction of +1 mm, and the slide the outline and pieces above
   * are drawn at (where the shape fits; absent: 0).
   */
  slide?: { dir: Pt; min: number; max: number; ref?: number };
}

export interface ZoneShapes {
  schema: 'foldmycard/zone-shapes@1' | 'foldmycard/zone-shapes@2';
  templateId: string;
  geometryHash: string;
  /** The view the two halves meet in (Opening 1). */
  meetView?: string;
  /**
   * By zone number. `pair`: zones set together (10 and 11). `y0`: where the
   * half's centre is, down the meeting view, unslid - two halves are lined up
   * when y0 + slide is the same for both. `legacy`: the slides of the
   * positions the first version offered (keys kind.align.size).
   */
  zones: Record<string, { default: string; pair?: number[]; y0?: number; legacy?: Record<string, number>; options: ZoneShapeOption[] }>;
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** The shape a zone has on a card, and how far it is slid (within its range). */
export function shapeOf(shapes: ZoneShapes, zone: number, chosen?: Record<string, string>, slide?: Record<string, number>) {
  const entry = shapes.zones[String(zone)];
  if (!entry) return null;
  let key = chosen?.[zone] ?? entry.default;
  let s = slide?.[zone];
  // the first version's keys - kind.align.size - are a kind, a size and a slide
  const parts = key.split('.');
  if (parts.length === 3) {
    key = `${parts[0]}.${parts[2]}`;
    s ??= entry.legacy?.[parts[1]];
  }
  const option = entry.options.find((o) => o.key === key) ?? entry.options.find((o) => o.key === entry.default)!;
  const by = option.slide ? clamp(s ?? 0, option.slide.min, option.slide.max) : 0;
  return { entry, option, slide: by };
}

/** The template with a card's chosen shapes, slid as it chose, in place of the designed ones. */
export function withShapes(
  t: TemplateDocument, shapes: ZoneShapes | null, chosen: Record<string, string> | undefined, slide?: Record<string, number>,
): TemplateDocument {
  if (!shapes || (!chosen && !slide) || shapes.geometryHash !== t.geometryHash) return t;
  const picks = new Map<number, { outline: Pt[]; extraOutlines: Pt[][]; areaMm2: number; up: Pt; visibleMm2: Record<string, number>;
    pieces: Record<string, TemplateViewPiece> }>();
  for (const zone of Object.keys(shapes.zones).map(Number)) {
    const got = shapeOf(shapes, zone, chosen, slide);
    if (!got || (got.option.key === got.entry.default && !got.slide)) continue;
    const { option: o } = got;
    // the pieces are this zone's, whatever number the model that made them read
    const own = (pieces: Record<string, TemplateViewPiece>) =>
      Object.fromEntries(Object.entries(pieces).map(([v, pc]) => [v, pc.zone === zone ? pc : { ...pc, zone }]));
    const by = got.slide - (o.slide?.ref ?? 0);
    if (!by) { picks.set(zone, { ...o, pieces: own(o.pieces) }); continue; }
    const d: Pt = [o.slide!.dir[0] * by, o.slide!.dir[1] * by];
    const move = (p: Pt[], m = d): Pt[] => p.map(([x, y]) => [x + m[0], y + m[1]]);
    const pieces: Record<string, TemplateViewPiece> = {};
    for (const [v, pc] of Object.entries(own(o.pieces))) {
      // the same move, seen in that view: the linear part of its page-to-view map
      const [a, b, c, e] = pc.pageToView;
      pieces[v] = { ...pc, outline: move(pc.outline, [a * d[0] + c * d[1], b * d[0] + e * d[1]]),
        pageToView: [a, b, c, e, pc.pageToView[4], pc.pageToView[5]] };
    }
    picks.set(zone, { ...o, outline: move(o.outline), extraOutlines: o.extraOutlines.map((x) => move(x)), pieces });
  }
  if (!picks.size) return t;
  return {
    ...t,
    zones: t.zones.map((z) => {
      const o = picks.get(z.number);
      return o ? { ...z, outline: o.outline, extraOutlines: o.extraOutlines, areaMm2: o.areaMm2, up: o.up, visibleMm2: o.visibleMm2 } : z;
    }),
    views: t.views.map((v) => ({
      ...v,
      pieces: v.pieces.map((pc) => picks.get(pc.zone)?.pieces[v.id] ?? pc),       // replaced, never added
    })),
  };
}
